import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Where the customer ends up when they leave the popup without adding anything.
 * Both accept 'checkout', 'cart' or 'none'.
 *
 * `SOFT_DISMISS_ACTION` covers the quieter ways out: escape, the backdrop, and
 * swiping the sheet away. Every dismissal ends up at the cart, because only
 * the primary button counts as the customer saying they are done shopping.
 */
const CLOSE_BUTTON_ACTION = 'cart';
const SOFT_DISMISS_ACTION = 'cart';

/**
 * Where the primary button sends the customer. `CONTINUE_ACTION` is the button
 * in its "Doorgaan zonder add-ons" state, `AFTER_ADD_ACTION` is the same button
 * once add-ons are selected and have been added to the cart.
 */
const CONTINUE_ACTION = 'checkout';
const AFTER_ADD_ACTION = 'cart';

/** How far down the sheet has to be dragged, in pixels, before it is dismissed. */
const SWIPE_DISMISS_DISTANCE = 80;

/**
 * Movement below this, in pixels, is still a tap. Kept small on purpose: the
 * gesture has to be claimed before the browser decides it owns it.
 */
const SWIPE_SLOP = 4;

/** Suffix of the alternate product template that renders the popup on its own. */
const UPSELL_VIEW = 'upsell';

/**
 * Formats an amount in cents using the shop's money format string.
 *
 * @param {number} cents - The amount in cents.
 * @param {string} format - The shop's money_format, e.g. `€{{amount_no_decimals}}`.
 * @returns {string} The formatted amount.
 */
function formatMoney(cents, format) {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '${{amount}}';

  /**
   * @param {number} number - The amount in cents.
   * @param {number} precision - Number of decimal places.
   * @param {string} thousands - Thousands delimiter.
   * @param {string} decimal - Decimal delimiter.
   * @returns {string} The delimited amount.
   */
  const withDelimiters = (number, precision, thousands, decimal) => {
    if (isNaN(number) || number == null) return '0';

    const parts = (number / 100.0).toFixed(precision).split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const fraction = parts[1] ? decimal + parts[1] : '';

    return whole + fraction;
  };

  const match = formatString.match(placeholderRegex);
  let value;

  switch (match && match[1]) {
    case 'amount_no_decimals':
      value = withDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = withDelimiters(cents, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = withDelimiters(cents, 0, ' ', ',');
      break;
    case 'amount_with_space_separator':
      value = withDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_with_period_and_space_separator':
      value = withDelimiters(cents, 2, ' ', '.');
      break;
    default:
      value = withDelimiters(cents, 2, ',', '.');
  }

  return formatString.replace(placeholderRegex, value);
}

/** @returns {(HTMLElement & { open?: () => void }) | null} The cart drawer, if the theme has one. */
function getCartDrawer() {
  return document.querySelector('cart-drawer-component');
}

/**
 * Stops the cart drawer opening itself on the next cart update.
 *
 * The drawer opens from a bubble-phase listener on `document`, so callers run
 * this from the capture phase to get in first. The returned function puts the
 * attribute back and is safe to call more than once.
 *
 * @returns {() => void} Restores the drawer's normal behaviour.
 */
function suppressDrawerAutoOpen() {
  const drawer = getCartDrawer();

  if (!drawer?.hasAttribute('auto-open')) return () => {};

  drawer.removeAttribute('auto-open');

  let restored = false;

  return () => {
    if (restored) return;
    restored = true;
    drawer.setAttribute('auto-open', '');
  };
}

/** @returns {string} The storefront root, keeping any locale prefix such as /en. */
function storeRoot() {
  return (Theme.routes.cart_url || '/cart').replace(/\/cart\/?$/, '');
}

/**
 * Sends the customer wherever leaving the popup should take them.
 *
 * @param {string} action - One of 'checkout', 'cart' or 'none'.
 */
function leaveTo(action) {
  if (action === 'checkout') {
    window.location.assign(`${storeRoot()}/checkout`);
    return;
  }

  if (action === 'cart') showCart();
}

/**
 * Shows the customer their cart, using the drawer when the theme has one.
 *
 * Unlike `openCartDrawer`, this always gets the customer to the cart, because it
 * is the destination they were promised rather than a nicety on top of an add.
 */
function showCart() {
  const drawer = getCartDrawer();

  if (typeof drawer?.open !== 'function') {
    window.location.assign(Theme.routes.cart_url || '/cart');
    return;
  }

  openCartDrawer();
}

/** Opens the cart drawer on the next frame, once any dialog has finished closing. */
function openCartDrawer() {
  const drawer = getCartDrawer();

  if (typeof drawer?.open !== 'function') return;

  requestAnimationFrame(() => drawer.open());
}

/**
 * The add-on upsell popup shown after a product is added to cart.
 *
 * The element owns its own UI only. Deciding when to show it lives in the
 * controller at the bottom of this file, because the popup also has to work on
 * pages where no instance exists yet, such as the wishlist.
 */
class UpsellPopup extends HTMLElement {
  /** @type {AbortController} Renewed on each connect so listeners survive a move. */
  #abortController;

  /** @type {{ moneyFormat: string, labels: Record<string, string>, variants: Record<string, {title: string, price: string, image: string}> }} */
  #data;

  /** @type {boolean} Guards against double submits. */
  #busy = false;

  /** @type {{ startX: number, startY: number, offset: number } | null} The swipe in progress. */
  #swipe = null;

  /** @type {boolean} Whether the last touch turned into a drag rather than a tap. */
  #swiped = false;

  connectedCallback() {
    const dataScript = this.querySelector('[data-upsell-data]');
    const dialog = this.querySelector('[data-upsell-dialog]');

    if (!dataScript || !(dialog instanceof HTMLDialogElement)) return;

    if (!this.#data) {
      try {
        this.#data = JSON.parse(dataScript.textContent || '{}');
      } catch (error) {
        console.error('[upsell-popup] Could not parse popup data', error);
        return;
      }
    }

    this.#abortController = new AbortController();

    const { signal } = this.#abortController;

    this.querySelector('[data-upsell-close]')?.addEventListener(
      'click',
      () => this.#dismiss(CLOSE_BUTTON_ACTION),
      { signal }
    );
    this.querySelector('[data-upsell-submit]')?.addEventListener('click', () => this.#submit(), { signal });

    dialog.addEventListener('cancel', this.#onCancel, { signal });
    dialog.addEventListener('click', this.#onDialogClick, { signal });

    this.addEventListener('change', this.#onSelectionChange, { signal });
    this.addEventListener('click', this.#onRowClick, { signal });

    // Runs before every other click listener here, so a swipe that ends on a
    // button does not also press it.
    this.addEventListener('click', this.#onClickCapture, { signal, capture: true });

    this.#sheet?.addEventListener('touchstart', this.#onTouchStart, { signal, passive: true });
    // Not passive: taking the gesture over from the browser needs preventDefault.
    this.#sheet?.addEventListener('touchmove', this.#onTouchMove, { signal, passive: false });
    this.#sheet?.addEventListener('touchend', this.#onTouchEnd, { signal });
    this.#sheet?.addEventListener('touchcancel', this.#onTouchEnd, { signal });

    this.#renderCta();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @returns {HTMLDialogElement} */
  get #dialog() {
    return /** @type {HTMLDialogElement} */ (this.querySelector('[data-upsell-dialog]'));
  }

  /** @returns {HTMLElement | null} The part that moves with a swipe: the panel and its close button. */
  get #sheet() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-upsell-sheet]'));
  }

  /** @returns {Element | null} The scrolling add-on list. */
  get #scroller() {
    return this.querySelector('[data-upsell-scroller]');
  }

  /** @returns {boolean} Whether the popup is currently showing. */
  get isOpen() {
    return Boolean(this.#dialog?.open);
  }

  /** @returns {HTMLInputElement[]} */
  get #checkboxes() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('[data-upsell-addon]')]);
  }

  /** @returns {HTMLInputElement[]} */
  get #selected() {
    return this.#checkboxes.filter((checkbox) => checkbox.checked && !checkbox.disabled);
  }

  /**
   * Opens the popup, showing the variant that was just added.
   *
   * @param {string | number | undefined} variantId - The variant added to cart.
   */
  open(variantId) {
    if (!this.#data || this.isOpen) return;

    const variant = this.#data.variants?.[String(variantId)];

    if (variant) {
      const image = this.querySelector('[data-upsell-added-image]');
      const title = this.querySelector('[data-upsell-added-variant]');
      const price = this.querySelector('[data-upsell-added-price]');

      if (image instanceof HTMLImageElement && variant.image) image.src = variant.image;
      if (title) title.textContent = variant.title;
      if (price) price.textContent = variant.price;
    }

    this.#setError('');
    this.#swiped = false;
    this.#resetSheet();
    this.#dialog.showModal();
  }

  /** @param {Event} event - The cancel event fired by the escape key. */
  #onCancel = (event) => {
    event.preventDefault();
    this.#dismiss(SOFT_DISMISS_ACTION);
  };

  /**
   * Closes the popup when the backdrop outside the panel is clicked.
   *
   * @param {MouseEvent} event - The click event.
   */
  #onDialogClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.upsell-popup__panel, .upsell-popup__close')) return;

    this.#dismiss(SOFT_DISMISS_ACTION);
  };

  /**
   * Starts tracking a possible swipe away.
   *
   * @param {TouchEvent} event - The touchstart event.
   */
  #onTouchStart = (event) => {
    this.#swiped = false;
    this.#swipe = null;

    if (event.touches.length !== 1) return;

    // A drag that begins part way down the list is the customer scrolling it.
    if ((this.#scroller?.scrollTop ?? 0) > 0) return;

    const touch = event.touches[0];

    if (!touch) return;

    this.#swipe = { startX: touch.clientX, startY: touch.clientY, offset: 0 };
  };

  /**
   * Drags the sheet along with the finger.
   *
   * @param {TouchEvent} event - The touchmove event.
   */
  #onTouchMove = (event) => {
    const swipe = this.#swipe;

    if (!swipe || event.touches.length !== 1) return;

    const touch = event.touches[0];

    if (!touch) return;

    const deltaX = touch.clientX - swipe.startX;
    const deltaY = touch.clientY - swipe.startY;

    // Too small to read a direction from yet, and possibly just a shaky tap.
    if (Math.abs(deltaY) < SWIPE_SLOP && Math.abs(deltaX) < SWIPE_SLOP) return;

    // Upwards or mostly sideways is not a dismissal, so hand the gesture back
    // to the browser and let it scroll the list.
    if (deltaY < 0 || Math.abs(deltaX) > Math.abs(deltaY)) {
      this.#settleSheet();
      this.#swipe = null;
      return;
    }

    // Claim the gesture the moment its direction is known. Waiting any longer
    // lets iOS start bouncing the list instead, and once it has, preventing
    // the default no longer does anything.
    if (event.cancelable) event.preventDefault();

    swipe.offset = deltaY - SWIPE_SLOP;
    this.#swiped = true;

    this.#offsetSheet(swipe.offset);
  };

  /** Dismisses the popup if the sheet was dragged far enough, or puts it back. */
  #onTouchEnd = () => {
    const swipe = this.#swipe;

    this.#swipe = null;

    if (!swipe) return;

    if (swipe.offset < SWIPE_DISMISS_DISTANCE) {
      this.#settleSheet();
      return;
    }

    this.#dismiss(SOFT_DISMISS_ACTION);
  };

  /**
   * Moves the sheet down by the given amount, following the finger exactly.
   *
   * @param {number} offset - The distance in pixels.
   */
  #offsetSheet(offset) {
    const sheet = this.#sheet;

    if (!sheet) return;

    sheet.classList.remove('upsell-popup__wrap--settling');
    sheet.style.transform = `translateY(${offset}px)`;
  }

  /** Slides the sheet back to where it started. */
  #settleSheet() {
    const sheet = this.#sheet;

    if (!sheet?.style.transform) return;

    sheet.classList.add('upsell-popup__wrap--settling');
    sheet.style.transform = '';
  }

  /** Puts the sheet back without animating, for reuse after it has closed. */
  #resetSheet() {
    const sheet = this.#sheet;

    if (!sheet) return;

    sheet.classList.remove('upsell-popup__wrap--settling');
    sheet.style.transform = '';
  }

  /**
   * Swallows the click that follows a swipe, so releasing over the call to
   * action does not also press it.
   *
   * @param {MouseEvent} event - The click event.
   */
  #onClickCapture = (event) => {
    if (!this.#swiped) return;

    this.#swiped = false;

    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Lets the whole row toggle its checkbox, so the tap target matches the card.
   *
   * @param {MouseEvent} event - The click event.
   */
  #onRowClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const row = event.target.closest('[data-upsell-item]');

    if (!row) return;
    // The product link and the checkbox itself already do the right thing.
    if (event.target.closest('a, input, label')) return;

    const checkbox = row.querySelector('[data-upsell-addon]');

    if (!(checkbox instanceof HTMLInputElement) || checkbox.disabled) return;

    checkbox.checked = !checkbox.checked;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /** @param {Event} event - The change event. */
  #onSelectionChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.hasAttribute('data-upsell-addon')) return;

    event.target.closest('.upsell-popup__item')?.classList.toggle('upsell-popup__item--selected', event.target.checked);

    this.#renderCta();
  };

  /** Updates the call to action to reflect the current selection. */
  #renderCta() {
    const label = this.querySelector('[data-upsell-cta-text]');

    if (!label) return;

    const selected = this.#selected;
    const { continueWithout, addSelected } = this.#data.labels ?? {};

    if (selected.length === 0) {
      label.textContent = continueWithout ?? '';
      return;
    }

    const total = selected.reduce((sum, checkbox) => sum + Number(checkbox.dataset.priceCents ?? 0), 0);

    label.textContent = (addSelected ?? '').replace('%%PRICE%%', formatMoney(total, this.#data.moneyFormat));
  }

  /** Handles the primary button: add the selection if there is one, then move on. */
  async #submit() {
    if (this.#busy) return;

    const variantIds = this.#selected.map((checkbox) => checkbox.value);

    if (variantIds.length === 0) {
      this.#close();
      leaveTo(CONTINUE_ACTION);
      return;
    }

    const button = this.querySelector('[data-upsell-submit]');
    const label = this.querySelector('[data-upsell-cta-text]');
    const previousLabel = label?.textContent ?? '';

    // The add below fires a cart update of its own. Without this the drawer
    // would slide open on top of the popup before it has closed.
    const restoreDrawer = suppressDrawerAutoOpen();

    this.#busy = true;
    this.#setError('');

    if (button instanceof HTMLButtonElement) button.disabled = true;
    if (label && this.#data.labels?.adding) label.textContent = this.#data.labels.adding;

    try {
      await this.#addToCart(variantIds);

      this.#close();
      leaveTo(AFTER_ADD_ACTION);
    } catch (error) {
      console.error('[upsell-popup] Could not add add-ons to cart', error);

      this.#setError(this.#data.labels?.error ?? '');

      if (label) label.textContent = previousLabel;
    } finally {
      this.#busy = false;
      restoreDrawer();

      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  }

  /**
   * Adds the selected add-ons and refreshes the rest of the cart UI.
   *
   * @param {string[]} variantIds - The variant ids to add.
   */
  async #addToCart(variantIds) {
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    /** @type {Record<string, unknown>} */
    const payload = {
      items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })),
    };

    // Only ask for re-rendered sections when there is a cart component to
    // refresh; an empty `sections` value is rejected by the cart API.
    if (sectionIds.length > 0) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || `Cart responded with ${response.status}`);
    }

    const sections = result.sections ?? {};

    document.dispatchEvent(
      new CartUpdateEvent({}, 'upsell-popup', {
        source: 'upsell-popup',
        itemCount: await this.#resolveItemCount(sections, sectionIds),
        sections,
      })
    );
  }

  /**
   * Reads the new cart count out of a re-rendered cart section, falling back to
   * the cart itself. The cart icon sets the bubble to whatever it is given, so
   * an undefined count here would blank the bubble out.
   *
   * @param {Record<string, string>} sections - The re-rendered sections.
   * @param {string[]} sectionIds - The section ids that were requested.
   * @returns {Promise<number>} The number of items in the cart.
   */
  async #resolveItemCount(sections, sectionIds) {
    for (const sectionId of sectionIds) {
      const html = sections[sectionId];

      if (!html) continue;

      const count = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]');

      if (count?.textContent) return parseInt(count.textContent, 10);
    }

    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());

    return cart.item_count ?? 0;
  }

  /**
   * Closes the popup without adding anything.
   *
   * @param {string} action - Where to send the customer next.
   */
  #dismiss(action) {
    this.#close();

    leaveTo(action);
  }

  #close() {
    this.#swipe = null;
    this.#dialog?.close();
  }

  /** @param {string} message - The error to show, or an empty string to clear it. */
  #setError(message) {
    const error = this.querySelector('[data-upsell-error]');

    if (!error) return;

    error.textContent = message;
    error.toggleAttribute('hidden', !message);
  }
}

if (!customElements.get('upsell-popup')) {
  customElements.define('upsell-popup', UpsellPopup);
}

/* -------------------------------------------------------------------------- */
/* Controller                                                                 */
/* -------------------------------------------------------------------------- */

/** @type {Map<string, Promise<UpsellPopup | null>>} In-flight fetches, keyed by handle. */
const pending = new Map();

/**
 * Gets the popup for a product, fetching and injecting it if this page does not
 * already have one. Product pages render their own inline, so nothing is
 * fetched there.
 *
 * @param {string} handle - The product handle.
 * @returns {Promise<UpsellPopup | null>} The popup, or null when the product has no add-ons to offer.
 */
async function loadPopup(handle) {
  const existing = document.querySelector(`upsell-popup[data-product-handle="${handle}"]`);

  if (existing instanceof UpsellPopup) return existing;

  const inFlight = pending.get(handle);

  if (inFlight) return inFlight;

  const request = (async () => {
    const response = await fetch(`${storeRoot()}/products/${encodeURIComponent(handle)}?view=${UPSELL_VIEW}`);

    if (!response.ok) throw new Error(`Upsell template responded with ${response.status}`);

    const markup = await response.text();
    const parsed = new DOMParser().parseFromString(markup, 'text/html').querySelector('upsell-popup');

    // An empty response is the normal answer for an excluded product or one
    // with no matching add-ons in stock.
    if (!parsed) return null;

    const popup = document.importNode(parsed, true);
    document.body.appendChild(popup);

    return popup instanceof UpsellPopup ? popup : null;
  })().finally(() => pending.delete(handle));

  pending.set(handle, request);

  return request;
}

/** Closes whatever overlay the customer added the product from. */
function closeSourceOverlays() {
  const quickView = /** @type {{ close?: () => void }} */ (window.productQuickView);

  if (typeof quickView?.close === 'function') quickView.close();

  const wishlistDrawer = /** @type {HTMLElement & { close?: () => void }} */ (
    document.querySelector('wishlist-drawer-component')
  );

  if (typeof wishlistDrawer?.close === 'function') wishlistDrawer.close();
}

/**
 * Decides whether a cart update should raise the popup, and from where.
 *
 * Runs in the capture phase so the cart drawer's own listener, which is on
 * `document` in the bubble phase, has not fired yet.
 *
 * @param {CustomEvent} event - The cart update event.
 */
function onCartUpdate(event) {
  const detail = event.detail?.data ?? {};

  if (detail.didError) return;

  // Added from the product page. The markup is already on the page.
  if (detail.source === 'product-form-component') {
    const popup = document.querySelector(`upsell-popup[data-product-id="${detail.productId}"]`);

    if (!(popup instanceof UpsellPopup) || popup.isOpen) return;

    const restoreDrawer = suppressDrawerAutoOpen();
    setTimeout(restoreDrawer);

    popup.open(event.detail?.sourceId);
    return;
  }

  // Added from the quick view, used on the wishlist and collection pages. The
  // product page is not loaded, so the markup has to be fetched.
  if (detail.source === 'product-quick-view') {
    if (!detail.handle) return;

    const restoreDrawer = suppressDrawerAutoOpen();

    loadPopup(detail.handle)
      .then((popup) => {
        // No popup means the product is excluded or has nothing in stock to
        // offer, so fall back to the drawer the theme would normally open.
        if (!popup) {
          openCartDrawer();
          return;
        }

        // Already showing: leave the customer where they are rather than
        // stacking the drawer on top of it.
        if (popup.isOpen) return;

        closeSourceOverlays();
        popup.open(detail.variantId);
      })
      .catch((error) => {
        console.error('[upsell-popup] Could not load the popup for', detail.handle, error);
        openCartDrawer();
      })
      .finally(restoreDrawer);
  }
}

document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate, { capture: true });
